/**
 * ============================================================================
 * EXECUTIVE SUMMARY PAGE — top-level route page composing the full dashboard.
 * ============================================================================
 *
 *
 * SCOPING: the demand-category checkbox row is the ONLY scoping mechanism —
 * the heading is static and the canonical route is /metrics. /network/:id is
 * kept as a redirect that pre-selects that checkbox (link stability).
 *
 * LAYOUT (matches Figma top-to-bottom):
 *   [Header: "KFE - WBR Detailed Summary" + RefreshStatusBadge]
 *   [FilterControlBar: pinned primaries + More filters + summary/counter]
 *   [DashboardTabs: Executive Summary | Deep Dive | WBR Snapshot | …]
 *   [Tab content: <ExecutiveSummaryTab> / <DeepDiveTab> / <GlossaryTab>]
 *
 * SNAPSHOT: not a separate tab — the page-level Live/Snapshot toggle switches
 * the Executive Summary between live data and the frozen WBR capture.
 *
 * TAB COMPONENT REGISTRY: maps view-config JSON `type` strings to React
 * components. Adding a new tab type = add one line here + one component in
 * src/components/tabs/. Adding a tab to an existing type = edit the JSON only.
 *
 * DATA FETCH: one call to useGetDataQuery per the whole page — all tabs and
 * tables draw from the same cached rowset (bulk-fetch-then-derive pattern).
 * Pass `rows` down or access from the RTK cache selector; do NOT fetch per tab.
 *
 * URL SYNC (TODO follow-up): activeTabId should sync to a query param
 * (?tab=executive-summary) so links are shareable within the WBR meeting.
 */

import React, { useEffect, Suspense } from 'react';
import { lazyWithRetry } from 'src/helpers/lazyWithRetry';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { PageHeader } from 'src/components/layout/PageHeader';
import { DashboardTabs } from 'src/components/layout/DashboardTabs';
import { RefreshStatusBadge } from 'src/components/layout/RefreshStatusBadge';
import { FilterControlBar } from 'src/components/filters/controls/FilterControlBar';
import {
  DEEP_DIVE_FILTER_PROPERTIES,
  PRIMARY_FILTER_KEYS,
  SUMMARY_FILTER_PROPERTIES,
} from 'src/config/filterConfig';
import { DemandCategoryCheckboxes } from 'src/components/filters/DemandCategoryCheckboxes';
import { ExecutiveSummaryTab } from 'src/components/tabs/ExecutiveSummaryTab';
import { GlossaryTab } from 'src/components/tabs/GlossaryTab';

import DataModeSelector from 'src/components/dashboard/DataModeSelector';
import { MetricTableSectionConfig, NetworkId } from 'src/types';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import {
  demandCategoriesReplaced,
  dataModeChanged,
  reportingWeekChanged,
} from 'src/features/dashboard/dashboardSlice';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { buildCatalogView } from 'src/features/api/catalogView';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import { ComingSoon } from 'src/components/common/ComingSoon';
import { WBR_REVIEW_TYPE } from 'src/constants';
import {
  availableAnchorWeeks,
  resolveWindow,
  toCanonicalWeekToken,
} from 'src/features/api/durationRange';
import { DEMAND_CATEGORY_IDS } from 'src/config/demandCategories';

/**
 * Tab types that own their whole surface (dive-deep-dcr-so spec R1.3, R5.1):
 * the page-level week picker / Live-Snapshot toggle and the global filter bar
 * hide while one is active.
 */
const SELF_CONTAINED_TAB_TYPES = new Set(['glossary', 'dive-deep-dcr', 'dive-deep-so']);

/**
 * Registry: view-config `type` → component. The component receives the tab's
 * `sections` as a prop (populated for 'table-sections' type, undefined for others).
 *
 * DEEP DIVE IS LAZY: it pulls in ag-grid, which is one of the two heavy static
 * dependencies in the bundle, and it is never the first tab rendered (the first
 * tab comes from viewConfig.tabs[0], the Executive Summary). Splitting it out
 * shrinks the initial bundle, which matters twice over: it moves FCP earlier and
 * it reduces the main-thread compile window that was inflating the bootstrap API
 * calls (GetWeekNumbers measured 135 ms server-side but 2,757 ms client-side).
 * webpackPrefetch fetches the chunk at low priority once the page is idle, so
 * clicking the tab does not wait on a cold request.
 */
const DeepDiveTab = lazyWithRetry(() =>
  import(/* webpackPrefetch: true */ 'src/components/tabs/DeepDiveTab').then(
    (module) => ({ default: module.DeepDiveTab }),
  ),
);

// The lifetime deep-dive tabs are lazy for the same reason (they share the
// ag-grid chunk and are never the first tab rendered).
const DiveDeepDcrTab = lazyWithRetry(() =>
  import(/* webpackPrefetch: true */ 'src/components/tabs/DiveDeepDcrTab').then(
    (module) => ({ default: module.DiveDeepDcrTab }),
  ),
);

const DiveDeepSoTab = lazyWithRetry(() =>
  import(/* webpackPrefetch: true */ 'src/components/tabs/DiveDeepSoTab').then(
    (module) => ({ default: module.DiveDeepSoTab }),
  ),
);

const TAB_COMPONENTS: Record<
  string,
  React.FC<{ sections?: MetricTableSectionConfig[] }>
> = {
  'table-sections': ExecutiveSummaryTab,
  'deep-dive': DeepDiveTab,
  'dive-deep-dcr': DiveDeepDcrTab,
  'dive-deep-so': DiveDeepSoTab,
  glossary: GlossaryTab,
};

export const ExecutiveSummaryPage: React.FC = () => {
  const { networkId } = useParams<{ networkId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const dataMode = useAppSelector((state) => state.dashboard.dataMode);

  // THE VIEW comes from the active dashboard's structure (multi-dashboard
  // R4.2): the '/metrics' page's hand-authored view config (KFE), or the
  // catalog-derived view ('catalog' — Quality) built from the definitions the
  // sections fetch anyway (cache hit, never an extra call).
  const activeDashboard = useActiveDashboard();
  const activeDashboardId = activeDashboard.dashboardId;
  const structure = activeDashboard.structure;
  const slug = activeDashboard.slug;

  // LEGACY ROUTE: <slug>/network/:networkId predates checkbox-only scoping.
  // Landing there selects that category's checkbox (known ids only) and
  // forwards to the canonical <slug>/metrics — old links keep working.
  useEffect(() => {
    if (networkId) {
      if ((DEMAND_CATEGORY_IDS as string[]).includes(networkId)) {
        dispatch(demandCategoriesReplaced([networkId as NetworkId]));
      }
      navigate(`/${slug}/detailed-view`, { replace: true });
    }
  }, [networkId, dispatch, navigate, slug]);
  const viewPage = structure.pages.find((p) => p.kind === 'view');
  const { data: definitionsResponse } = useGetMetricDefinitionsQuery(
    { dashboardId: activeDashboardId, reviewType: WBR_REVIEW_TYPE },
    // Skipped while coming-soon (placeholder needs nothing) and for
    // hand-authored views (their sections fetch the catalog themselves).
    { skip: structure.comingSoon === true || viewPage?.view !== 'catalog' },
  );
  const viewConfig = React.useMemo(
    () =>
      viewPage?.view === 'catalog' || !viewPage?.view
        ? buildCatalogView(definitionsResponse?.definitions)
        : viewPage.view,
    [viewPage, definitionsResponse],
  );

  // TAB ↔ URL: the active tab derives from `?tab=` (spec
  // metric-definition-tooltips R2.2/R2.3) — shareable links, working
  // back/forward, and in-app deep links (the metric tooltips' "View in
  // glossary"). An unknown or absent value falls back to the first tab, which
  // also covers a dashboard switch swapping the tab set: no reset effect
  // needed, the derivation self-corrects.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const activeTabId = viewConfig.tabs.some((t) => t.id === urlTab)
    ? (urlTab as string)
    : viewConfig.tabs[0]?.id || '';
  const handleTabChange = (tabId: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tabId);
        // A MANUAL tab switch drops any deep-link target (?term=, ?dcr=) —
        // the glossary highlight / SO drill-in focus belongs to the link
        // that carried it, not to every later visit.
        next.delete('term');
        next.delete('dcr');
        return next;
      },
      // Tab switches replace, so Back leaves the page rather than replaying
      // every tab the user flipped through.
      { replace: true },
    );
  };

  const activeTab = viewConfig.tabs.find((t) => t.id === activeTabId);
  const TabComponent = activeTab ? TAB_COMPONENTS[activeTab.type] : null;

  // The page's VISIBLE week window — the same inputs every section resolves
  // its window from (week controls + mode), built once here to scope the
  // filter bar's population counter to what is actually on screen.
  const reportingWeek = useAppSelector((s) => s.dashboard.reportingWeek);
  const { data: weekData } = useGetWeekNumbersQuery();
  const isSnapshot = dataMode === 'snapshot';
  const durationWindow = React.useMemo(
    () =>
      weekData
        ? resolveWindow(
            weekData.weeks,
            weekData.currentWeek,
            reportingWeek,
            isSnapshot,
          )
        : null,
    [weekData, reportingWeek, isSnapshot],
  );
  const visibleWeeks = durationWindow?.durationRange;

  // Header week picker (consistent controls: week first, then Live/Snapshot,
  // on every data page — the old WeekControls pill in the filter zone is
  // gone). Options and selection resolution match the Home/Domain pages:
  // the shared anchor universe, the pick when the mode can anchor on it,
  // else the resolved default the tables actually show.
  const availableWeeks = React.useMemo(
    () =>
      weekData
        ? availableAnchorWeeks(weekData.weeks, weekData.currentWeek, isSnapshot)
        : [],
    [weekData, isSnapshot],
  );
  // Canonicalize before matching — availableWeeks is canonical; a persisted
  // raw reportingWeek anchors the data fine but would miss this includes().
  const canonicalReporting = reportingWeek
    ? toCanonicalWeekToken(reportingWeek)
    : null;
  const selectedWeekForPicker =
    canonicalReporting && availableWeeks.includes(canonicalReporting)
      ? canonicalReporting
      : durationWindow
      ? toCanonicalWeekToken(durationWindow.anchor)
      : null;

  // A dashboard being onboarded shows the placeholder body — no tabs,
  // filters, or data surfaces (multi-dashboard: quality.comingSoon).
  if (structure.comingSoon) {
    return (
      <SpaceBetween direction="vertical" size="l">
        <PageHeader />
        <ComingSoon label={activeDashboard.label} />
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Top bar: header + refresh badge on the left, back link + Live/Snapshot toggle pinned right */}
      <div className="page-top-bar">
        <SpaceBetween direction="horizontal" size="m">
          <PageHeader />
          <RefreshStatusBadge />
        </SpaceBetween>
        {/* Page-level navigation lives in the page-tabs strip (spec
            navigation-model R2.2) — the back link-button is gone. The
            glossary is a static reference and the lifetime deep-dive tabs
            are week-agnostic (dive-deep-dcr-so spec D4/R1.3): no data mode,
            no week, so the whole control pair hides on all three. Redux
            week/mode state is untouched — leaving the tab restores the
            controls as they were. */}
        {!SELF_CONTAINED_TAB_TYPES.has(activeTab?.type ?? '') && (
          <DataModeSelector
            mode={dataMode}
            selectedWeek={selectedWeekForPicker}
            availableWeeks={availableWeeks}
            onModeChange={(m) => dispatch(dataModeChanged(m))}
            onWeekChange={(w) => dispatch(reportingWeekChanged(w))}
          />
        )}
      </div>

      {/* Tab navigation (driven by view config JSON) */}
      <DashboardTabs
        tabs={viewConfig.tabs}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
      />

      {/* Network checkbox row ABOVE the filter system (QuickSight control-bar
          order, spec R10.1) — table-sections tabs only. */}
      {activeTab?.type === 'table-sections' && <DemandCategoryCheckboxes />}

      {/* Filter system: chip bar + week window controls (hidden on glossary
          since it's a static reference). No Fragment here — SpaceBetween
          flattens Fragment children in React 18 and warns (each child must
          be a direct element). Each tab OFFERS its own property set
          (QuickSight per-sheet controls: the deep dive has no Metric
          Group/Name; the summaries have no Time Period/Committed Week…) while
          the filter STATE stays global (deep-dive spec D3). */}
      {!SELF_CONTAINED_TAB_TYPES.has(activeTab?.type ?? '') && (
        <FilterControlBar
          properties={
            activeTab?.type === 'deep-dive'
              ? DEEP_DIVE_FILTER_PROPERTIES
              : SUMMARY_FILTER_PROPERTIES
          }
          primaryKeys={
            activeTab?.type === 'deep-dive'
              ? PRIMARY_FILTER_KEYS.deepDive
              : PRIMARY_FILTER_KEYS.summary
          }
          weeks={visibleWeeks}
          // Deep dive: Time Period shows the visible window CHECKED by
          // default (virtual — follows the window; deep-dive spec R4.3).
          defaultSelections={
            activeTab?.type === 'deep-dive' && visibleWeeks
              ? { timePeriod: visibleWeeks }
              : undefined
          }
        />
      )}
      {/* Tab content — rendered BELOW tabs, not inside them */}
      {/* Suspense covers the lazy Deep Dive chunk. Fallback is null rather than a
          spinner: the chunk is prefetched, so in practice it resolves within a frame,
          and a flashing spinner would be worse than nothing. */}
      {TabComponent && (
        <Suspense fallback={null}>
          <TabComponent sections={activeTab?.sections} />
        </Suspense>
      )}

      {/* The unified callout panel is mounted ONCE at app level (App.tsx),
          available on every page/tab in snapshot mode — spec metric-callouts
          R1.6. Cell clicks here still drive it via calloutCellSelected. */}
    </SpaceBetween>
  );
};
